import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Props ────────────────────────────────────────────────────────────────────

interface SignaturePadProps {
  /** Fires after each completed stroke with the canvas PNG data URL, or `null` on clear. */
  onChange: (dataUrl: string | null) => void;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
//
// Canvas signature capture that works with mouse, touch, AND stylus. Uses Pointer
// Events (not mouse-only) so it draws on a back-room iPad. The canvas backing store
// is sized to the rendered box × devicePixelRatio (and the context scaled to match)
// so strokes land where the pen touches and stay crisp on retina screens.

export default function SignaturePad({ onChange, className }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Configure the 2D context for crisp, smooth black strokes.
  const configureContext = useCallback((ctx: CanvasRenderingContext2D, dpr: number) => {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // map CSS px → device px
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
  }, []);

  // Size the backing store to the rendered box × DPR. Runs on mount and resize.
  // Resizing the canvas clears it, so we reset ink state and notify the parent.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    configureContext(ctx, dpr);
    if (hasInkRef.current) {
      hasInkRef.current = false;
      setHasInk(false);
      onChange(null);
    }
  }, [configureContext, onChange]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  // Translate a pointer event into canvas-local CSS pixel coordinates.
  function pointFor(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const pt = pointFor(e);
    lastPointRef.current = pt;
    // Dot for a tap so a single touch leaves a mark.
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
    ctx.lineTo(pt.x + 0.01, pt.y + 0.01);
    ctx.stroke();
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    const pt = pointFor(e);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
  }

  // End of a stroke: emit the current canvas as a PNG data URL.
  function finishStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    hasInkRef.current = true;
    setHasInk(true);
    onChange(canvas.toDataURL('image/png'));
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawingRef.current = false;
    lastPointRef.current = null;
    hasInkRef.current = false;
    setHasInk(false);
    onChange(null);
  }

  return (
    <div className={cn('relative', className)}>
      <canvas
        ref={canvasRef}
        className="block h-[140px] w-full rounded-md border border-input bg-background"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
        onPointerCancel={finishStroke}
      />
      <button
        type="button"
        onClick={handleClear}
        disabled={!hasInk}
        className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded border bg-background/80 px-2 py-1 text-xs font-medium text-muted-foreground backdrop-blur transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Eraser className="h-3 w-3" />
        Clear
      </button>
      {!hasInk && (
        <span className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-muted-foreground/60">
          Sign above
        </span>
      )}
    </div>
  );
}
